import { useEffect, useRef, useState } from 'react';
import { useInputAction } from '../keymap';

export type CancelState = 'ready' | 'awaiting-confirm' | 'cancelling';

export const CANCEL_HINT: Record<CancelState, string> = {
  ready: '*Esc* to cancel',
  'awaiting-confirm': '*Esc* again to confirm cancel',
  cancelling: 'Cancelling...',
};

const CANCEL_CONFIRM_TIMEOUT_MS = 3_000;

/**
 * Manages the two-step ESC cancel confirm flow during a loading stream.
 *
 * State machine:
 *   ready → (ESC) → awaiting-confirm → (ESC within 3s) → cancelling → (loading stops) → ready
 *   ready → (ESC) → awaiting-confirm → (3s timeout) → ready
 *
 * The keymap dispatcher only routes `stream.cancel` here while loading, so the
 * Escape key never leaks to other handlers during a stream. While a prompt is
 * queued, Esc is rebound to `queue.cancel`; we reset awaiting-confirm in that
 * case so a stale confirm-window can't fire on the first post-queue Esc.
 */
export function useCancelStream(
  isLoading: boolean,
  hasQueuedPrompt: boolean,
  onCancelStream: () => void,
): CancelState {
  const [cancelState, setCancelState] = useState<CancelState>('ready');

  // Ref avoids stale closure when a second keypress races the re-render.
  const cancelStateRef = useRef(cancelState);
  cancelStateRef.current = cancelState;

  useInputAction('stream.cancel', () => {
    if (cancelStateRef.current === 'ready') {
      setCancelState('awaiting-confirm');
    } else if (cancelStateRef.current === 'awaiting-confirm') {
      setCancelState('cancelling');
      onCancelStream();
    }
  });

  useEffect(() => {
    if (cancelState !== 'awaiting-confirm') return undefined;
    const timer = setTimeout(() => setCancelState('ready'), CANCEL_CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [cancelState]);

  useEffect(() => {
    if (!isLoading || hasQueuedPrompt) {
      setCancelState('ready');
    }
  }, [isLoading, hasQueuedPrompt]);

  return cancelState;
}
